import React from 'react';
import PropTypes from 'prop-types';
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Typography} from '@mui/material';

// AD-2692 (Maria, 14 October 2026): when a form cannot be submitted, the person completing it sees why and what to do,
// in a pop-up that stays until they close it, instead of a red message that disappears after a few seconds.
const QuestionList = ({items, onGoTo}) => (
    <Box component="ul" sx={{m: 0, pl: 2.5}}>
        {items.map(item => (
            <li key={item.id}>
                <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => onGoTo(item.id)}
                    sx={{textAlign: 'left'}}
                    data-testid="submit-problem-question"
                >
                    {item.text}
                </Link>
                {item.error && (
                    <Typography variant="body2" color="text.secondary" component="span">{` (${item.error})`}</Typography>
                )}
            </li>
        ))}
    </Box>
);

QuestionList.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({id: PropTypes.string, text: PropTypes.string, error: PropTypes.string})),
    onGoTo: PropTypes.func,
};

const SubmitProblemDialog = ({
                                 open,
                                 onClose,
                                 missing = [],
                                 format = [],
                                 payment = false,
                                 medicine = [],
                                 sections = [],
                                 childName,
                                 profileHref,
                                 serverMessage,
                                 onGoTo,
                             }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="submit-problem-dialog">
        <DialogTitle>This form has not been submitted</DialogTitle>
        <DialogContent>
            <Stack spacing={2}>
                {serverMessage && <Typography>{serverMessage}</Typography>}
                {sections.length > 0 && (
                    <Box>
                        <Typography sx={{mb: 0.5}}>
                            {`Before you can submit this form, please confirm these sections of ${childName ? `${childName}'s` : 'the child\'s'} profile.`}
                        </Typography>
                        <Box component="ul" sx={{m: 0, pl: 2.5}}>
                            {sections.map(section => <li key={section}><Typography variant="body2">{section}</Typography></li>)}
                        </Box>
                        {profileHref && (
                            <Button variant="outlined" size="small" href={profileHref} sx={{mt: 1}}>
                                Open the profile
                            </Button>
                        )}
                    </Box>
                )}
                {missing.length > 0 && (
                    <Box>
                        <Typography sx={{mb: 0.5}}>Please answer these questions, then click on Submit again.</Typography>
                        <QuestionList items={missing} onGoTo={onGoTo}/>
                    </Box>
                )}
                {format.length > 0 && (
                    <Box>
                        <Typography sx={{mb: 0.5}}>Please check these answers, then click on Submit again.</Typography>
                        <QuestionList items={format} onGoTo={onGoTo}/>
                    </Box>
                )}
                {payment && (
                    <Typography>
                        This form needs a payment before it can be submitted. Please click on Make Payment and complete
                        the payment, then click on Submit again.
                    </Typography>
                )}
                {medicine.length > 0 && (
                    <Box>
                        <Typography sx={{mb: 0.5}}>Please check the medicine boxes in these questions, then click on Submit again.</Typography>
                        <QuestionList items={medicine} onGoTo={onGoTo}/>
                    </Box>
                )}
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button variant="contained" onClick={onClose} data-testid="submit-problem-close">Close</Button>
        </DialogActions>
    </Dialog>
);

SubmitProblemDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    missing: PropTypes.array,
    format: PropTypes.array,
    payment: PropTypes.bool,
    medicine: PropTypes.array,
    sections: PropTypes.arrayOf(PropTypes.string),
    childName: PropTypes.string,
    profileHref: PropTypes.string,
    serverMessage: PropTypes.string,
    onGoTo: PropTypes.func,
};

export default SubmitProblemDialog;
